import React from 'react';
import PropTypes from 'prop-types';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import InputContainer from '../input-container/InputContainer';
import Components from '../../../common/Components';
import joinClassNames from "../../../common/JoinClassNames";
import ValidationEngine from "../../../common/validation/ValidationEngine";
import ValidationType from "../../../common/validation/ValidationType";
import DirtyEngine from "../../../common/dirty/DirtyEngine";
import ChildPosition, {positionChildren} from "../ChildPosition";

const propTypes = {
	// children node that allows you to pass in other elements
	children: PropTypes.node,

	// put the children before the label or after the input
	childrenPosition: PropTypes.oneOf([
		ChildPosition.AFTER,
		ChildPosition.BEFORE,
	]),

	// additional classes to put on the component
	className: PropTypes.string,

	// tracks dirty state of fields
	dirtyEngine: PropTypes.instanceOf(DirtyEngine),

	// if true then the input will be disabled
	disabled: PropTypes.bool,

	// An error message to show. Overrides validation errors.
	errorMessage: PropTypes.string,

	// the name of the redux field being edited and id of the input for label. for=
	field: PropTypes.string.isRequired,

	// is this input "dirty" - trumps DirtyEngine
	isDirty: PropTypes.bool,

	// the label/placeholder to show
	label: PropTypes.string.isRequired,

	// triggers when the field loses focus
	onBlur: PropTypes.func,

	// what function to trigger when a change is detected (could be a dispatcher); parameters: field, value
	onChange: PropTypes.func.isRequired,

	// if true then the input will be disabled
	readOnly: PropTypes.bool,

	// When the component mounts should the input be given focus.
	setInitialFocus: PropTypes.bool,

	// should the label be shown?
	showLabel: PropTypes.bool,

	// the validation rules and results
	validationEngine: PropTypes.instanceOf(ValidationEngine),

	// the value of this field: should be a string/number that can be put in an input (not an object)
	value: PropTypes.bool,

};

const defaultProps =  {
	children: undefined,
	childrenPosition: ChildPosition.AFTER,
	className: undefined,
	dirtyEngine: undefined,
	disabled: false,
	errorMessage: undefined,
	isDirty: undefined,
	onBlur: undefined,
	readOnly: false,
	setInitialFocus: false,
	showLabel: true,
	validationEngine: new ValidationEngine(),
	value: false,
};

export default class CheckBox extends React.Component {
	constructor(props) {
		super(props);

		this.checkboxRef = React.createRef();
		this.dirtyEngine = this.props.dirtyEngine ? this.props.dirtyEngine : new DirtyEngine({[this.props.field]: this.props.value});

		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.positionChildren = positionChildren.bind(this);

		this.state = {
			hasBlurred: false
		};
	}

	componentDidMount() {
		if (this.props.setInitialFocus) {
			this.checkboxRef.current.focus();
		}
	}

	onBlur() {
		this.setState({hasBlurred: true});
		if (this.props.onBlur) {
			this.props.onBlur(...arguments);
		}
	}

	onChange(e) {
		if (this.props.onChange) {
			this.props.onChange(this.props.field, e.target.checked);
		}
	}

	render() {
		const validationError = this.props.validationEngine.getFormError(this.props.field, this.state.hasBlurred, this.props.errorMessage);
		const required = this.props.validationEngine.fieldHasRule(this.props.field, ValidationType.REQUIRED);
		return (
			<InputContainer className={joinClassNames(
				this.props.className,
				required ? 'required' : '',
				validationError ? 'error' : undefined,
				CommonAppSetup.getClassName(Components.CHECKBOX, 'wrapper'),
				this.dirtyEngine.isDirtyClassName({[this.props.field]: this.props.value}, this.props.field, this.props.isDirty)
			)}>
				{positionChildren(this, ChildPosition.BEFORE)}
				<label
					htmlFor={this.props.field}
					className={joinClassNames(this.props.showLabel ? '' : 'visually-hidden', CommonAppSetup.getClassName(Components.CHECKBOX, 'label'))}
				>{this.props.label}</label>
				<input
					checked={this.props.value}
					className={joinClassNames(
						this.props.showLabel ? '' : 'no-label',
						CommonAppSetup.getClassName(Components.CHECKBOX, 'input')
					)}
					disabled={this.props.disabled}
					id={this.props.field}
					name={this.props.field}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={this.props.readOnly}
					ref={this.checkboxRef}
					required={required ? 'required' : undefined}
					type="checkbox"
				/>
				{positionChildren(this, ChildPosition.AFTER)}
				{validationError}
			</InputContainer>
		);
	}
}

CheckBox.propTypes = propTypes;
CheckBox.defaultProps = defaultProps;